/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { page as SamplePage } from '../../help/sample_pages/TabsPage';
import { ItemType } from '../../registry/Base.type';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { tabPanelOrientation, tabPanelVariant } from '../../registry/constants';
import { defaultValue, TabPanel, WrapperProps } from './TabPanel';

const TabPanelManager: ComponentManagerFn<'TabPanel', WrapperProps> = (
  platform: Platform,
): ComponentManager<'TabPanel', WrapperProps> => {
  return {
    ...baseComponentManager<'TabPanel', WrapperProps>(platform, 'TabPanel'),
    isChildAllowed: (page, item, childType) =>
      (['TabItem'] as ItemType[]).includes(childType),
    defaultValue,
    defaultChildren: ['TabItem', 'TabItem', 'TabItem'],
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'hideTitle':
          return itemData.compact;
        case 'disableHoverEffect':
        case 'compact':
        case 'iconSize':
        case 'tabHeaderPadding':
        case 'centered':
        case 'indicatorColor':
        case 'variant':
        case 'orientation':
          return !itemData.hideHeader;
        default:
          return true;
      }
    },
    props: {
      activeTab: {
        type: 'ItemID',
        label: 'Active Tab',
        initialFilter: 'TabItem',
        category: 'Tabs',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const tabs = platform.getChildrenItemsById(
            appUid,
            pageId,
            itemId,
            (c) => c.type === 'TabItem' && c.itemId === value,
          );
          if (!tabs.length) {
            return `Unable to find a tab with this item id '${value}' under this tab panel!`;
          }
          return '';
        },
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        helpText: 'Defines the number of grids the component is going to use.',
        category: 'Spacing',
      },
      centered: {
        label: 'Centered',
        type: 'boolean',
        helpText: 'If checked, the tabs will be centered',
        category: 'Tabs',
      },
      indicatorColor: {
        label: 'Indicator Color',
        type: 'ThemeBGColor',
        helpText: 'The color of tab when selected.',
        category: 'Tabs',
        filterColor: (color) => color !== 'paper',
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: tabPanelVariant,
        category: 'Tabs',
      },
      orientation: {
        label: 'Orientation',
        type: 'select',
        options: tabPanelOrientation,
        category: 'Tabs',
      },
      hideHeader: {
        label: 'Hide Header',
        type: 'boolean',
        helpText:
          'You can use this to hide all the tab headers, to mimic a card panel layout.',
        category: 'Header',
      },
      disableHoverEffect: {
        label: 'Disable Hover Effect',
        type: 'boolean',
        category: 'Header',
      },
      compact: {
        label: 'Icon On Top',
        type: 'boolean',
        category: 'Header',
      },
      hideTitle: {
        label: 'Hide Title',
        type: 'boolean',
        helpText:
          'If checked, the tabs will show icon and title as tooltip. Else title will be shown below icon',
        category: 'Header',
      },
      iconSize: {
        label: 'Icon Size',
        type: 'number',
        min: 16,
        max: 80,
        step: 4,
        category: 'Header',
      },
      tabHeaderPadding: {
        label: 'Tab Header Padding',
        type: 'padding',
        category: 'Header',
      },
      updateURL: {
        label: 'Update URL',
        type: 'boolean',
        helpText:
          'If checked, the URL will be updated with the active tab and will be restored on browser refresh',
        category: 'Tabs',
      },
    },
    name: 'Tab Panel',
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    getWrapperProps: ({ item, platform: _platform, pageId, appUid }) => ({
      setActiveTab: (value: string) => {
        _platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          item.itemId,
          'activeTab',
          value,
        );
      },
    }),
    onChildMoveOut: ({ appUid, pageId, value, child }) => {
      // design prop update
      if (child.type === 'TabItem' && value.props.activeTab === child.itemId) {
        value.props = { ...value.props, activeTab: '' };
      }
      // runtime prop update
      if (
        platform.getRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
        ) === child.itemId
      ) {
        platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
          '',
        );
      }
    },
    onChildMoveIn: ({ appUid, pageId, value, child }) => {
      if (child.type === 'TabItem') {
        platform.setRuntimeProp<TabPanel>(
          appUid,
          pageId,
          value.itemId,
          'activeTab',
          child.itemId,
        );
      }
    },
    icon: { iconName: 'io-tab-panel', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Tab Panel is a navigation container used to organize content into distinct, switchable sections. Unlike a list or a grid that displays everything at once, the Tab Panel groups related content at the same hierarchical level, allowing users to toggle between views (like "Overview," "Analytics," and "Settings") while staying on the same page.
It serves as the parent controller for Tab Items. While the Panel handles the logic, layout, and state, the Items hold the actual content.

**1. Navigation & State Management**<br/>
The Tab Panel manages which view is currently visible to the user.
* **Active Tab:** Sets the default tab that is open when the page loads. You can bind this to a variable to control the active state programmatically (e.g., switching tabs based on a button click elsewhere).
* **Update URL:** A powerful feature for deep linking. If enabled, the component automatically syncs the ID of the active tab to the browser's URL. If a user refreshes the page or bookmarks the URL, they will return to the exact same tab they were viewing.

**2. Orientation & Layout**<br/>
You can adapt the structure of the tabs to fit your screen real estate.
* **Orientation:** Controls the alignment of the tab headers. (Horizontal appears in a row above content; Vertical appears as a list to the left).
* **Constraint:** Vertical orientation is designed for desktop layouts and requires a minimum screen width of 1200px. If the screen is smaller, it may revert or behave unexpectedly.
* **Centered:** If enabled, horizontal tab headers are aligned to the center of the container rather than the left. This is often used for main page navigation with few items.

**3. Header Appearance**<br/>
These properties allow you to fine-tune the look of the navigation strip (the "Header").
* **Variant:** Selects the visual style (e.g., Standard, Scrollable, Full Width).
* **Indicator Color:** Customizes the color of the active tab underline/highlighter.
* **Hide Header:** Removes the navigation strip entirely. Use this to create a "Wizard" or "Card Switcher" where the visible tab is controlled strictly by logic (buttons like "Next" / "Back") rather than user selection.
* **Compact (Icon On Top):** Stacks the icon above the text label for a taller, narrower tab style.
* **Hide Title:** Hides the text label completely. The title will appear as a tooltip when the user hovers over the icon.
* **Disable Hover Effect:** Removes the background highlight that usually appears when hovering over a tab header.

**4. Spacing & Sizing**<br/>
* **Icon Size:** Explicitly sets the pixel size of the icons in the header (Min: 16px, Max: 80px).
* **Tab Header Padding:** Controls the internal spacing around the text/icon within the header button.
* **Padding:** Controls the spacing inside the content area (where the child components render).

**5. Structure**<br/>
The Tab Panel has a strict relationship with its children.
* **Allowed Children:** You can only add Tab Item components directly to a Tab Panel. Adding other components (like Buttons or Grids) directly to the root of the panel is not supported; they must be placed inside a Tab Item.`,
    helpRenders: [
      {
        title: 'Basic Tabs',
        description: 'Basic Tab Panel with Three Tabs',
        hideProps: true,
        page: SamplePage,
        exampleId: 'e1',
        visibleProps: [],
        item: {
          itemId: 'tabPanel',
          props: {},
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Tabs With Icon',
        description: 'Tabs with both Title & Icon',
        page: SamplePage,
        exampleId: 'e2',
        visibleProps: ['compact'],
        item: {
          itemId: 'tabPanel',
          props: {},
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Tabs Orientation',
        description: `Tabs with Vertical Orientation.

\`\`\`alert
Note: Veritcal Orientation will be in effect only when the available screen width is atleast 1200px.
\`\`\`
`,
        page: SamplePage,
        exampleId: 'e3',
        visibleProps: ['orientation'],
        height: 240,
        item: {
          itemId: 'tabPanel',
          props: { orientation: 'vertical' },
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Icon Only',
        description: 'Tabs with Icon On Top & Hide Title turned on',
        page: SamplePage,
        exampleId: 'e4',
        visibleProps: ['hideTitle', 'compact', 'hideTitle'],
        height: 240,
        item: {
          itemId: 'tabPanel',
          props: { orientation: 'vertical', compact: true, hideTitle: true },
          children: ['tabItem', 'tabItem1', 'tabItem2'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Icon Size & Padding',
        description: 'Tabs with larger icon & spacing around it',
        page: SamplePage,
        exampleId: 'e5',
        visibleProps: ['compact', 'iconSize', 'tabHeaderPadding'],
        height: 400,
        item: {
          itemId: 'tabPanel',
          props: {
            orientation: 'vertical',
            compact: true,
            iconSize: 80,
            tabHeaderPadding: { all: 40 },
          },
          children: ['tabItem', 'tabItem1'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Update URL',
        description:
          "With Update URL prop turned on, the Active Tab state is stored in the URL and upon browser refresh the active tab get's restored from the URL state. This allows the URL to be bookmarked along with the active tab state.",
        page: SamplePage,
        exampleId: 'e6',
        visibleProps: ['updateURL'],
        item: {
          itemId: 'tabPanel',
          props: { updateURL: true },
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
      {
        title: 'Hide Header',
        page: SamplePage,
        exampleId: 'e7',
        visibleProps: ['hideHeader'],
        item: {
          itemId: 'tabPanel',
          props: { hideHeader: true },
          children: ['tabItem3', 'tabItem4', 'tabItem5'],
          type: 'TabPanel',
          parent: 'root',
        },
      },
    ],
  };
};

export default TabPanelManager;
